import defaultTo from 'lodash/defaultTo';
import { INCREASED_MAX_CUSTOMER_CONTACTS } from '@css-xp/ppc-constants';
import { parse } from 'tldts';
import type { ContactInfo, InvoiceGroup } from '@css-xp/graphtypes/src/generated-types-new';
import { generatedTypesNew } from '@css-xp/graphtypes';

export const getDomainFromEmail = (email: string) => {
  /* The 'tldts' library is designed to parse URLs and hostnames. To use it with an email address,
   * we extract the server part (everything after the '@' symbol) and pass it to 'tldts' for parsing.
   * This allows us to retrieve the main domain.
   *
   * Example:
   * - If the email is 'test@in.india.com':
   *   - serverPartOfEmail = 'in.india.com'
   *   - parsedDomain = 'india.com'
   */
  const serverPartOfEmail = email.split('@')[1];
  const parsedDomain = parse(serverPartOfEmail).domain;
  return parsedDomain;
};

export const sortBillingProfiles = (billingProfiles: any[], isSortEnabled: boolean, sortByPaymentMethod: boolean) => {
  function sortByName(a: InvoiceGroup, b: InvoiceGroup) {
    return a?.name?.localeCompare(b?.name ?? '') ?? 0;
  }

  if (sortByPaymentMethod && isSortEnabled) {
    const PaymentMethodScore = {
      [generatedTypesNew.PaymentMethodType.CARD]: 1,
      [generatedTypesNew.PaymentMethodType.DEFERRED]: 2,
      [generatedTypesNew.PaymentMethodTypeV2.PAYPAL]: 3,
    };
    return billingProfiles.sort((a, b) => {
      const paymentType1 = a?.defaultPaymentMethod?.type as generatedTypesNew.PaymentMethodType;
      const paymentType2 = b?.defaultPaymentMethod?.type as generatedTypesNew.PaymentMethodType;
      if (paymentType1 && paymentType2) {
        if (paymentType1 === paymentType2) {
          return sortByName(a, b);
        }

        const billingMethod1 = PaymentMethodScore[paymentType1]; //card 1
        const billingMethod2 = PaymentMethodScore[paymentType2]; // def - 2
        return billingMethod1 - billingMethod2;
      }
      if (!paymentType1 && paymentType2) {
        return 1;
      }
      if (paymentType1 && !paymentType2) {
        return -1;
      }
      return 0;
    });
  }

  return isSortEnabled ? billingProfiles.sort((a, b) => sortByName(a, b)) : billingProfiles;
};

export const getMaxLimitOfCustomerContact = () => {
  return INCREASED_MAX_CUSTOMER_CONTACTS;
};

// A Billing profile is considered to be IUL if ship to Party is null and primary contact email domain is in partner domain list
export const getIsBillingProfileUsedForInternalUsageLicense = ({
  eccDetails,
  shipToPartyId,
  relatedDomains,
}: {
  eccDetails: ContactInfo[];
  shipToPartyId: string | undefined | null;
  relatedDomains: string[] | undefined;
}) => {
  const primaryContact = eccDetails?.find((contact: ContactInfo) => contact?.isPrimary);
  if (primaryContact?.email && !shipToPartyId && defaultTo(relatedDomains, []).length > 0) {
    const domainFromPrimaryCustomerContact = getDomainFromEmail(primaryContact?.email);
    if (
      domainFromPrimaryCustomerContact &&
      defaultTo(relatedDomains, []).includes(domainFromPrimaryCustomerContact.toLowerCase())
    ) {
      return true;
    }
  }
  return false;
};

/** primary contact must not belong to partner domain for customer billing profile */
export const isValidPrimaryContactDomain = ({
  email,
  partnerDomains,
}: {
  email: string;
  partnerDomains: string[] | null | undefined;
}) => {
  const domainFromPrimaryCustomerContact = getDomainFromEmail(email);

  if (
    domainFromPrimaryCustomerContact &&
    defaultTo(partnerDomains, []).includes(domainFromPrimaryCustomerContact.toLowerCase())
  ) {
    return false;
  }
  return true;
};
